import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';

// Cache for user card data to prevent unnecessary reloads
const userCardCache = {
  data: null,
  timestamp: null,
  expiryMs: 5 * 60 * 1000, // Cache for 5 minutes
};

export const useUserCard = () => {
  const { user } = useAuth();
  const [userCard, setUserCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const isFetchingRef = useRef(false);

  useEffect(() => {
    const fetchUserCard = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      // Prevent duplicate fetches
      if (isFetchingRef.current) {
        return;
      }

      // Check cache first
      const now = Date.now();
      if (userCardCache.data && 
          userCardCache.timestamp && 
          (now - userCardCache.timestamp) < userCardCache.expiryMs) {
        console.log('Using cached user card data');
        setUserCard(userCardCache.data);
        setLoading(false);
        return;
      }

      try {
        isFetchingRef.current = true;
        const { cardAPI } = await import('../services/api');
        
        // Use getMyCards instead of getAll to fetch only current user's cards
        const myCards = await cardAPI.getMyCards();
        
        // Get the first card (should be the user's primary card)
        const myCard = Array.isArray(myCards) && myCards.length > 0 ? myCards[0] : null;
        
        // Update cache
        userCardCache.data = myCard;
        userCardCache.timestamp = Date.now();
        
        setUserCard(myCard);
      } catch (error) {
        console.error('Error fetching user card:', error);
        setUserCard(null);
      } finally {
        setLoading(false);
        isFetchingRef.current = false;
      }
    };

    fetchUserCard();
  }, [user]);

  return { userCard, loading };
};

// Function to invalidate cache when user updates their card
export const invalidateUserCardCache = () => {
  userCardCache.data = null;
  userCardCache.timestamp = null;
};
